import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import { select } from '@swf/core/store';

export interface FeatureFlagData {
  data: { features: FeatureFlag };
}

export interface CountryStateData {
  values: { country: string; state?: string };
}

export enum Guard {
  AuthenticationCheck = 'authenticationCheck',
  AutoTrigerRmf = 'autoTrigerRmf',
  SignupCountryAvailable = 'signupCountryAvailable',
  SignupRmfEnabled = 'signupRmfEnabled',
  SkipSignupCountry = 'skipSignupCountry',
  ProvinceCheck = 'provinceCheck',
  SingleScreen = 'singleScreen'
}

export const isSiteUS = (site: Sites): boolean =>
  [Sites.US_NJ, Sites.US_PA, Sites.USMI].includes(site);
export const isSiteCAON = (site: Sites): boolean => site === Sites.CA_ON;
export const isSiteCOM = (site: Sites): boolean => site === Sites.COM;

const provinceCAON = 'ON';

const useSignupGuards = () => {
  const site = useSelector(select.cms.getSiteId);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const { country, region } = useSelector(select.location.getQuery);
  const authenticationCheck = useCallback(() => isAuthenticated, [isAuthenticated]);

  const checkSignupRmfFeature = useCallback(
    ({ data: { features } }: FeatureFlagData) => features[FeatureFlag.SwcSignupRmf],
    []
  );

  const showSingleScreen = useCallback(
    ({ data: { features } }: FeatureFlagData) => features[FeatureFlag.SwcSignupOnePage],
    []
  );

  const skipSignupCountry = useCallback(() => {
    if (isSiteUS(site)) return true;
    return (
      country === Country.Canada &&
      ((isSiteCOM(site) && region !== provinceCAON) ||
        (isSiteCAON(site) && region === provinceCAON))
    );
  }, [country, region, site]);

  const checkCountryAvailable = useCallback(
    ({ data: { isCountryAvailable } }: { data: { isCountryAvailable: boolean } }) =>
      isCountryAvailable,
    []
  );

  const checkRmfAutoTrigger = useCallback(
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.WrmfAcAutotrigger],
    []
  );

  const provinceCheck = useCallback(
    ({ values: { country, state } }: CountryStateData) =>
      country === Country.Canada &&
      ((isSiteCOM(site) && state === provinceCAON) || (isSiteCAON(site) && state !== provinceCAON)),
    [site]
  );

  return {
    [Guard.AuthenticationCheck]: authenticationCheck,
    [Guard.AutoTrigerRmf]: checkRmfAutoTrigger,
    [Guard.SignupCountryAvailable]: checkCountryAvailable,
    [Guard.SignupRmfEnabled]: checkSignupRmfFeature,
    [Guard.SkipSignupCountry]: skipSignupCountry,
    [Guard.ProvinceCheck]: provinceCheck,
    [Guard.SingleScreen]: showSingleScreen
  };
};

export default useSignupGuards;
